/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (`packages/core-rust/src/auth/rbac.rs`).
 * The back-office UI uses this to (a) decide whether a user may enter the admin
 * area at all and (b) hide/disable individual actions a role can't perform. The
 * backend remains the source of truth and re-checks every mutation; this is
 * purely a UX layer so the client never offers an action that would 403.
 *
 * Both sides are tied to `rbac-contract.json`, the single source for the role
 * hierarchy: `ROLE_RANK` below is DERIVED from it at runtime and the tests on
 * each side assert their own map against it. Before that contract existed each
 * side only checked itself — which is exactly how the backend became more
 * permissive than this file without anything going red.
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags/content, but not destructive removals and
 *                   never granting a role above its own
 *   - support     — read users + audit, ban/unban + revoke sessions + content
 *                   (no role/flag changes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

import contract from "./rbac-contract.json";

export const ADMIN_ROLES = ["super_admin", "admin", "support", "read_only", "user"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on. */
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  | "users.remove"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "content.read"
  | "content.write"
  | "metrics.read"
  // MONETIZATION_QUOTAS: read and mutate limits per tier, ads, and subscriptions.
  | "billing.read"
  | "billing.write"
  // CONFIG_OVER_CODE: view and edit OPERATIONAL configuration (limits,
  // retention, lockout, languages, modules). `Capability::ViewSettings` /
  // `Capability::ManageSettings`. Leer es read_only+; escribir es admin+.
  | "settings.read"
  | "settings.write";

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "content.read",
  "metrics.read",
  "settings.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  "integrations.testEmail",
  "content.write",
];

const ADMIN_CAPS: AdminCapability[] = [
  ...SUPPORT_CAPS,
  "users.setRole",
  "flags.write",
  // Monetization: same level as flags (changes behavior at runtime).
  "billing.read",
  "billing.write",
  // Operational config: same level as flags, and for the same reason — changes
  // server behavior dynamically, without redeploy.
  "settings.write",
];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  read_only: READ_CAPS,
  user: [],
};

/**
 * Role hierarchy, DERIVED from the shared contract rather than restated here.
 *
 * Written key by key on purpose instead of `Object.fromEntries(...) as Record<…>`:
 * the explicit literal makes the compiler check that the contract actually
 * carries all five roles, whereas the cast would just believe it.
 */
export const ROLE_RANK: Record<AdminRole, number> = {
  super_admin: contract.roles.super_admin.rank,
  admin: contract.roles.admin.rank,
  support: contract.roles.support.rank,
  read_only: contract.roles.read_only.rank,
  user: contract.roles.user.rank,
};

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role. */
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none). */
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability. */
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all. */
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

/**
 * The roles `actorRole` may GRANT — never above its own rank.
 *
 * Replaces the old `ASSIGNABLE_ROLES` constant, which was the whole list for
 * everybody. The name is the point: "assignable" never forced anyone to say
 * *assignable by whom*, so the user manager happily offered `super_admin` to an
 * `admin`, and the backend accepted it because it only validated that the role
 * existed. The signature forces the right question.
 *
 * Mirrors `can_grant_role` in `packages/core-rust/src/auth/rbac.rs`.
 */
export function assignableRolesFor(actorRole: string | null | undefined): readonly AdminRole[] {
  const actor = toAdminRole(actorRole);
  if (!roleCan(actor, "users.setRole")) {
    return [];
  }
  return ADMIN_ROLES.filter((role) => ROLE_RANK[role] <= ROLE_RANK[actor]);
}

/**
 * True when `actorRole` may act on a user whose CURRENT role is `targetRole`
 * (ban, revoke sessions, reset MFA, remove, change role).
 *
 * Strictly below one's own rank, except `super_admin`, who may act on peers —
 * otherwise a rogue `super_admin` would be untouchable. Mirrors
 * `can_administer` in Rust.
 */
export function canAdministerUser(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toAdminRole(actorRole);
  if (actor === "super_admin") {
    return true;
  }
  return ROLE_RANK[toAdminRole(targetRole)] < ROLE_RANK[actor];
}
